import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Heading } from "react-aria-components";
import { useEffect, useState } from "react";
import { Icon } from "./icons.js";
import { joinClassNames } from "./utils.js";
export function ProductShell({ children, className, mobileNavigation, navigation, utility }) {
    return _jsxs("div", { className: joinClassNames("ss-product-shell", Boolean(utility) && "ss-product-shell--with-utility", className), children: [_jsx("aside", { className: "ss-product-shell__sidebar", children: navigation }), _jsx("main", { className: "ss-product-shell__stage", children: children }), utility ? _jsx("aside", { className: "ss-product-shell__utility", children: utility }) : null, mobileNavigation ? _jsx("nav", { className: "ss-product-shell__mobile-nav", "aria-label": "Mobile Space navigation", children: mobileNavigation }) : null] });
}
export function Sidebar({ children, className }) {
    return _jsx("div", { className: joinClassNames("ss-sidebar", className), children: children });
}
export function SidebarSection({ children, className, label }) {
    return _jsxs("section", { className: joinClassNames("ss-sidebar-section", className), children: [label ? _jsx("h2", { children: label }) : null, children] });
}
export function SidebarFooter({ children }) {
    return _jsx("footer", { className: "ss-sidebar-footer", children: children });
}
export function Stage({ children, className, scrollOwner = "space-stage" }) {
    return _jsx("div", { className: joinClassNames("ss-stage", className), "data-scroll-owner": scrollOwner, children: children });
}
export function Screen({ children, className, narrow = false }) {
    return _jsx("div", { className: joinClassNames("ss-screen", narrow && "ss-screen--narrow", className), children: children });
}
export function ScreenHeader({ actions, children, className, eyebrow, title }) {
    return _jsxs("header", { className: joinClassNames("ss-screen-header", className), children: [_jsxs("div", { children: [eyebrow ? _jsx("div", { className: "ss-eyebrow", children: eyebrow }) : null, _jsx(Heading, { level: 1, children: title }), children ? _jsx("div", { className: "ss-screen-header__description", children: children }) : null] }), actions ? _jsx("div", { className: "ss-screen-header__actions", children: actions }) : null] });
}
export function PaneLayout({ children, className, variant = "list-detail" }) {
    return _jsx("div", { className: joinClassNames("ss-pane-layout", `ss-pane-layout--${variant}`, className), children: children });
}
export function useStoredPaneSize(storageKey, defaultSize, minimum, maximum) {
    const clamp = (value) => Math.min(maximum, Math.max(minimum, Math.round(value)));
    const [size, setSize] = useState(() => {
        if (typeof window === "undefined")
            return clamp(defaultSize);
        const stored = Number(window.localStorage.getItem(`snowiespace.pane.${storageKey}`));
        return Number.isFinite(stored) ? clamp(stored) : clamp(defaultSize);
    });
    useEffect(() => {
        if (typeof window !== "undefined")
            window.localStorage.setItem(`snowiespace.pane.${storageKey}`, String(clamp(size)));
    }, [maximum, minimum, size, storageKey]);
    return [clamp(size), (next) => setSize(clamp(next))];
}
export function PaneResizer({ className, defaultSize, label, maximum, minimum, onChange, resize = "before", value }) {
    const change = (next) => onChange(Math.min(maximum, Math.max(minimum, Math.round(next))));
    const onPointerDown = (event) => {
        if (event.button !== 0)
            return;
        const startX = event.clientX;
        const startValue = value;
        const target = event.currentTarget;
        target.setPointerCapture(event.pointerId);
        const move = (moveEvent) => change(startValue + (moveEvent.clientX - startX) * (resize === "before" ? 1 : -1));
        const finish = (finishEvent) => {
            target.releasePointerCapture(finishEvent.pointerId);
            target.removeEventListener("pointermove", move);
            target.removeEventListener("pointerup", finish);
            target.removeEventListener("pointercancel", finish);
        };
        target.addEventListener("pointermove", move);
        target.addEventListener("pointerup", finish);
        target.addEventListener("pointercancel", finish);
    };
    const onKeyDown = (event) => {
        const direction = resize === "before" ? 1 : -1;
        if (event.key === "ArrowLeft") {
            event.preventDefault();
            change(value - 12 * direction);
        }
        else if (event.key === "ArrowRight") {
            event.preventDefault();
            change(value + 12 * direction);
        }
        else if (event.key === "Home") {
            event.preventDefault();
            change(minimum);
        }
        else if (event.key === "End") {
            event.preventDefault();
            change(maximum);
        }
        else if (event.key === "Enter") {
            event.preventDefault();
            change(defaultSize);
        }
    };
    return _jsx("div", { "aria-label": label, "aria-orientation": "vertical", "aria-valuemax": maximum, "aria-valuemin": minimum, "aria-valuenow": value, className: joinClassNames("ss-pane-resizer", className), onDoubleClick: () => change(defaultSize), onKeyDown: onKeyDown, onPointerDown: onPointerDown, role: "separator", tabIndex: 0, title: "Drag to resize. Arrow keys adjust; Enter resets.", children: _jsx("span", { "aria-hidden": "true" }) });
}
export function Pane({ as: Component = "section", children, className, scrollOwner, tone = "surface" }) {
    return _jsx(Component, { className: joinClassNames("ss-pane", `ss-pane--${tone}`, className), "data-scroll-owner": scrollOwner, children: children });
}
export function PaneHeader({ actions, children, className, eyebrow, title }) {
    return _jsxs("header", { className: joinClassNames("ss-pane-header", className), children: [_jsxs("div", { children: [eyebrow ? _jsx("div", { className: "ss-eyebrow", children: eyebrow }) : null, _jsx(Heading, { level: 2, children: title }), children ? _jsx("div", { className: "ss-pane-header__description", children: children }) : null] }), actions ? _jsx("div", { className: "ss-pane-header__actions", children: actions }) : null] });
}
export function ScrollRegion({ as: Component = "div", children, className, name, tabIndex }) {
    return _jsx(Component, { className: joinClassNames("ss-scroll-region", className), "data-scroll-owner": name, tabIndex: tabIndex, children: children });
}
export function Stack({ align, children, className, gap = "4" }) {
    return _jsx("div", { className: joinClassNames("ss-stack", className), style: { "--ss-stack-gap": `var(--ss-space-${gap})`, alignItems: align }, children: children });
}
export function Cluster({ align = "center", children, className, gap = "3", justify = "flex-start", wrap = true }) {
    return _jsx("div", { className: joinClassNames("ss-cluster", className), style: { "--ss-cluster-gap": `var(--ss-space-${gap})`, alignItems: align, justifyContent: justify, flexWrap: wrap ? "wrap" : "nowrap" }, children: children });
}
export function Grid({ children, className, columns = "auto", gap = "4" }) {
    return _jsx("div", { className: joinClassNames("ss-grid", `ss-grid--${columns}`, className), style: { "--ss-grid-gap": `var(--ss-space-${gap})` }, children: children });
}
export function Toolbar({ ariaLabel, children, className }) {
    return _jsx("div", { className: joinClassNames("ss-toolbar", className), role: "toolbar", "aria-label": ariaLabel, children: children });
}
export function Card({ action, children, className, eyebrow, title }) {
    return _jsxs("section", { className: joinClassNames("ss-card", className), children: [_jsxs("header", { className: "ss-card__header", children: [_jsxs("div", { children: [eyebrow ? _jsx("div", { className: "ss-eyebrow", children: eyebrow }) : null, _jsx(Heading, { level: 2, className: "ss-card__title", children: title })] }), action ? _jsx("div", { className: "ss-card__action", children: action }) : null] }), _jsx("div", { className: "ss-card__body", children: children })] });
}
export function Panel({ children, className, footer, heading, tone = "surface" }) {
    return _jsxs("section", { className: joinClassNames("ss-panel", `ss-panel--${tone}`, className), children: [heading ? _jsx("header", { className: "ss-panel__header", children: heading }) : null, _jsx("div", { className: "ss-panel__body", children: children }), footer ? _jsx("footer", { className: "ss-panel__footer", children: footer }) : null] });
}
export function ListRow({ actions, children, className, description, icon, meta, title }) {
    return _jsxs("div", { className: joinClassNames("ss-list-row", className), children: [icon ? _jsx("span", { className: "ss-list-row__icon", children: _jsx(Icon, { name: icon }) }) : null, _jsxs("div", { className: "ss-list-row__content", children: [_jsx("strong", { children: title }), description ? _jsx("small", { children: description }) : null, children] }), meta ? _jsx("div", { className: "ss-list-row__meta", children: meta }) : null, actions ? _jsx("div", { className: "ss-list-row__actions", children: actions }) : null] });
}
export function DefinitionList({ children, className }) {
    return _jsx("dl", { className: joinClassNames("ss-definition-list", className), children: children });
}
